import { useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import {
  Box, Divider, Typography, Stack, MenuItem, Avatar, Chip, IconButton,
} from '@mui/material';
import { MenuPopover } from '../../components/MenuPopover';
import Iconify from '../../components/Iconify';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { logout, selectAuth } from '../../app/services/auth/authSlice';
import { accountPhotoURL } from 'definitions/constant/misc';
import { getUserInfo, getRole } from 'Service/Service';
import { ROLE_COLORS, ROLE_LABELS, PRIMARY } from 'themes/colors';

// ----------------------------------------------------------------------

const MENU_OPTIONS = [
  { label: 'Home', icon: 'eva:home-fill', linkTo: '/' },
  { label: 'Profile', icon: 'eva:person-fill', linkTo: '/account' },
];

// ----------------------------------------------------------------------

export function AccountPopover() {
  const anchorRef = useRef(null);
  const dispatch = useAppDispatch();
  const auth = useAppSelector(selectAuth);
  const [open, setOpen] = useState<HTMLElement | null>(null);
  const loggedInUser = getUserInfo();
  const role = getRole();
  const accentColor = ROLE_COLORS[role] || ROLE_COLORS.admin;

  const handleOpen = (event: any) => setOpen(event.currentTarget);
  const handleClose = () => setOpen(null);

  return (
    <>
      <IconButton
        ref={anchorRef}
        onClick={handleOpen}
        sx={{
          p: 0.5,
          borderRadius: '50%',
          border: '2px solid',
          borderColor: open ? 'primary.main' : 'transparent',
          transition: 'border-color 0.2s',
          '&:hover': { borderColor: alpha(PRIMARY.main, 0.4) },
        }}
      >
        <Avatar
          src={loggedInUser?.profile_url || accountPhotoURL}
          alt={auth.user?.name}
          sx={{ width: 34, height: 34, bgcolor: 'primary.main', fontSize: 14 }}
        >
          {auth.user?.name?.[0]?.toUpperCase()}
        </Avatar>
      </IconButton>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          p: 0,
          mt: 1.5,
          ml: 0.75,
          width: 230,
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          '& .MuiMenuItem-root': {
            typography: 'body2',
            borderRadius: 1.5,
          },
        }}
      >
        {/* User header */}
        <Box sx={{ px: 2.5, py: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar
              src={loggedInUser?.profile_url || accountPhotoURL}
              sx={{ width: 38, height: 38, bgcolor: 'primary.main', fontSize: 14 }}
            >
              {auth.user?.name?.[0]?.toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={700} noWrap>
                {auth.user?.name}
              </Typography>
              <Chip
                label={ROLE_LABELS[role] || role}
                size="small"
                sx={{
                  height: 18,
                  fontSize: 10,
                  fontWeight: 700,
                  mt: 0.25,
                  bgcolor: alpha(accentColor, 0.12),
                  color: accentColor,
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            </Box>
          </Stack>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          {MENU_OPTIONS.map((option) => (
            <MenuItem
              key={option.label}
              to={option.linkTo}
              component={RouterLink}
              onClick={handleClose}
              sx={{ gap: 1.5, py: 1 }}
            >
              <Iconify
                icon={option.icon}
                sx={{ width: 18, height: 18, color: 'text.secondary', flexShrink: 0 }}
              />
              {option.label}
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Box sx={{ p: 1 }}>
          <MenuItem
            onClick={() => {
              dispatch(logout());
              handleClose();
              localStorage.removeItem('jhpuser');
              localStorage.removeItem('jhpToken');
              window.location.href = '/login';
            }}
            sx={{
              gap: 1.5, py: 1,
              color: 'error.main',
              '&:hover': { bgcolor: alpha('#FF4842', 0.08) },
            }}
          >
            <Iconify
              icon="eva:log-out-fill"
              sx={{ width: 18, height: 18, flexShrink: 0 }}
            />
            Logout
          </MenuItem>
        </Box>
      </MenuPopover>
    </>
  );
}
